import { http } from '@app/lib/api'
import { type AlertTone, type PublicLiveAlert, sortAlerts } from '@shared/contracts/festival'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router'

/** Every tone carries a text label (and a symbol), so color is never the only signal. */
export const TONE_LABEL: Record<AlertTone, string> = {
  urgent: 'Urgent',
  warning: 'Heads up',
  info: 'Update',
}
export const TONE_ICON: Record<AlertTone, string> = { urgent: '⚠', warning: '▲', info: 'ℹ' }

export function ToneTag({ tone }: { tone: AlertTone }) {
  return (
    <span className="live-alert__tag">
      <span aria-hidden="true">{TONE_ICON[tone]}</span>
      {TONE_LABEL[tone]}
      <span className="sr-only">:</span>
    </span>
  )
}

/** A staff-supplied link: site paths route in-app, full URLs are ordinary links. */
export function AlertLink({ href, children }: { href: string; children: ReactNode }) {
  return href.startsWith('/') ? <Link to={href}>{children}</Link> : <a href={href}>{children}</a>
}

/** Drops alerts that expired since the payload was fetched (or cached for offline use). */
export function stillActive(alerts: PublicLiveAlert[], now = Date.now()) {
  return alerts.filter((a) => !a.expiresAt || Date.parse(a.expiresAt) > now)
}

const DISMISSED_KEY = 'bp.liveAlerts.dismissed'

function readDismissed(): string[] {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY)
    const v = raw ? JSON.parse(raw) : []
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

function writeDismissed(ids: string[]) {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-50)))
  } catch {
    /* private mode or storage full: dismissal lasts for this page view only */
  }
}

/**
 * Site-wide festival alert strip. Shows the single most important active alert (urgent, then
 * warning, then info; newest first). Polls every minute while the tab is visible; each alert
 * can be dismissed once per browser. Hidden on /live, which shows every alert itself.
 */
export function LiveAlertBanner() {
  const { pathname } = useLocation()
  const onLive = pathname === '/live'
  const q = useQuery({
    queryKey: ['public', 'alerts'],
    queryFn: () => http.get<{ at: string; items: PublicLiveAlert[] }>('/api/v1/public/alerts'),
    // TanStack pauses interval refetches while the tab is hidden.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    enabled: !onLive,
  })
  const [dismissed, setDismissed] = useState<string[]>(readDismissed)
  if (onLive) return null
  const top = sortAlerts(stillActive(q.data?.items ?? [])).find((a) => !dismissed.includes(a.id))
  if (!top) return null
  const dismiss = () => {
    const next = [...dismissed, top.id]
    setDismissed(next)
    writeDismissed(next)
  }
  return (
    <section className={`live-alert live-alert--${top.tone}`} aria-label="Festival alert">
      <ToneTag tone={top.tone} />
      <span className="live-alert__msg">
        {top.message}{' '}
        {top.link ? (
          <>
            <AlertLink href={top.link}>Details</AlertLink>
            {' · '}
          </>
        ) : null}
        <Link to="/live">Festival live updates</Link>
      </span>
      <button
        type="button"
        className="live-alert__close"
        onClick={dismiss}
        aria-label="Dismiss this alert"
      >
        <span aria-hidden="true">×</span>
      </button>
    </section>
  )
}
